import { Button, Dialog, DialogActions, DialogContent, Divider, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Stack } from '@mui/system';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Project } from '../../Projects/projects';
import { projectSlice } from '../../../redux/ProjectSlice';
import { isArray } from 'lodash';
import { useState } from 'react';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { useNavigate } from 'react-router-dom';
import { duplicateProject } from '../../../utils/projectDuplication';

interface DuplicateProjectWarningProps {
  onClose: () => void;
  open: boolean;
  projects: Array<Project> | undefined;
  isMultipleProjects: boolean;
}

const DuplicateProjectWarning = ({ onClose, open, projects, isMultipleProjects }: DuplicateProjectWarningProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedActions, setSelectedActions] = useState(() => {
    if (isMultipleProjects && isArray(projects)) {
      return projects.reduce<{ [key: string]: string }>((acc, curr) => {
        acc[curr.name] = 'replace';
        return acc;
      }, {});
    }
    return {};
  });

  const handleActionChange = (projectName: string, action: string) => {
    setSelectedActions((prev) => ({
      ...prev,
      [projectName]: action,
    }));
  };

  const handleCopy = (project: Project) => {
    if (!project) return;
    const copyName = t('projects.copyOf', { name: project.name });
    const copiedProject = duplicateProject(project, copyName);
    dispatch(projectSlice.actions.onAddProject(copiedProject));
    dispatch(projectSlice.actions.onLoadProject(copiedProject));
    window.projectId = copiedProject.id;
    onClose();
    navigate(`/editor/${copiedProject.id}`);
  };

  const handleReplace = (project: Project) => {
    if (!project) return;
    dispatch(projectSlice.actions.onReplaceProject(project));
    dispatch(projectSlice.actions.onLoadProject(project));
    window.projectId = project.id;
    onClose();
    navigate(`/editor/${project.id}`);
  };

  const handleMultipleProjects = () => {
    if (!projects) return;
    projects.forEach((project) => {
      if (selectedActions[project.name] === 'copy') {
        console.log(project.name);
        handleCopy({ ...project, name: project.name });
      } else {
        handleReplace(project);
      }
    });
    onClose();
    dispatch(applicationSlice.actions.onOpenDialog({ dialog: null }));
  };

  return (
    <Dialog onClose={onClose} open={open} scroll={'paper'} maxWidth={!isMultipleProjects ? 'sm' : 'md'}>
      <DialogTitle variant="sectionTitle" align="center">
        <Trans i18nKey="warningDialog.title">Warning</Trans>
      </DialogTitle>
      <Divider />
      <DialogContent style={{ padding: '3rem 2.75rem' }}>
        {!isMultipleProjects ? (
          <>
            <Typography variant="body1" fontWeight={500}>
              <Trans i18nKey="duplicateProjectDialog.body">
                The project you are loading is already saved. Do you want to replace that saved project with your upload, or make a copy?
              </Trans>
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body1" fontWeight={500}>
              {projects && projects.length === 1 ? (
                <Trans i18nKey="duplicateProjectDialog.body" />
              ) : (
                <Trans i18nKey="duplicateProjectDialog.multipleBody">
                  You have uploaded multiple projects that already exist. For each project, choose whether to replace the existing one or make a copy.
                </Trans>
              )}
            </Typography>
            {projects &&
              projects.map((project) => (
                <Box key={project.name} style={{ marginTop: '1rem' }}>
                  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="flex-start">
                    <Typography variant="body1" fontWeight={500}>
                      {project.name}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />

                    <FormControl component="fieldset">
                      <RadioGroup row value={selectedActions[project.name]} onChange={(e) => handleActionChange(project.name, e.target.value)}>
                        <FormControlLabel value="copy" control={<Radio />} label={<Trans i18nKey="button.make_a_copy">Make a copy</Trans>} />
                        <FormControlLabel value="replace" control={<Radio />} label={<Trans i18nKey="button.replace">Replace</Trans>} />
                      </RadioGroup>
                    </FormControl>
                  </Stack>
                </Box>
              ))}
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions style={!isMultipleProjects ? { justifyContent: 'center', margin: '2px 0' } : { margin: '2px 12px' }}>
        {!isMultipleProjects ? (
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Button variant="text" autoFocus onClick={() => handleCopy(projects ? projects[0] : ({} as Project))}>
              <Trans i18nKey="button.make_a_copy">Make a copy</Trans>
            </Button>
            <Button variant="contained" autoFocus onClick={() => handleReplace(projects ? projects[0] : ({} as Project))}>
              <Trans i18nKey="button.replace">Replace</Trans>
            </Button>
          </Stack>
        ) : (
          <Button variant="contained" autoFocus onClick={handleMultipleProjects}>
            <Trans i18nKey="uploadFileDialog.submit">Submit</Trans>
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateProjectWarning;
